import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Expand, ListVideo, Minimize, Pause, Play, RotateCcw, SkipForward, Volume2, VolumeX } from "lucide-react";
import { authApi, contentApi, mediaUrl } from "@services/contentApi";
import Header from "@components/Header";
import Footer from "@components/Footer";
import "@styles/player.css";

const formatTime = (seconds = 0) => {
  const value = Number.isFinite(seconds) ? seconds : 0;
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const secs = Math.floor(value % 60);
  return `${hours ? `${hours}:` : ""}${hours ? String(minutes).padStart(2, "0") : minutes}:${String(secs).padStart(2, "0")}`;
};

export default function PlayerPage() {
  const { id } = useParams();
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const resumeAtRef = useRef(0);
  const controlsTimerRef = useRef(null);
  const lastProgressSaveRef = useRef(0);
  const progressEnabledRef = useRef(false);
  const [content, setContent] = useState(null);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState("");
  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [resumeTarget, setResumeTarget] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    contentApi.getContent(id).then(async (data) => {
      if (!active) return;
      setContent(data);
      const firstEpisode = data.type === "series" ? data.seasons?.[0]?.episodes?.[0] : null;
      let initialEpisode = firstEpisode;
      let savedPosition = 0;
      try {
        const { progress } = await authApi.contentWatchProgress(data._id);
        if (!active) return;
        progressEnabledRef.current = true;
        savedPosition = Number(progress?.position || 0);
        if (progress?.episodeId) initialEpisode = data.seasons?.flatMap((season) => season.episodes || []).find((item) => item._id === progress.episodeId) || firstEpisode;
      } catch (_error) {
        progressEnabledRef.current = false;
      }
      const sources = initialEpisode?.videoSources || data.videoSources || [];
      setSelectedEpisodeId(initialEpisode?._id || "");
      setSelectedSourceId(sources[0]?._id || "");
      if (savedPosition > 0) setResumeTarget({ episodeId: initialEpisode?._id || "", position: savedPosition });
    }).catch((err) => active && setError(err.response?.data?.message || err.message));
    return () => { active = false; };
  }, [id]);

  const episodes = useMemo(() => (content?.seasons || []).flatMap((season) => (season.episodes || []).map((episode) => ({ ...episode, seasonNumber: season.seasonNumber }))), [content]);
  const episode = useMemo(() => episodes.find((item) => item._id === selectedEpisodeId) || episodes[0], [episodes, selectedEpisodeId]);
  const sources = useMemo(() => content?.type === "series" ? episode?.videoSources || [] : content?.videoSources || [], [content, episode]);
  const selectedSource = sources.find((source) => source._id === selectedSourceId) || sources[0];
  const intro = episode?.intro || content?.intro || {};
  const credits = episode?.credits || content?.credits || {};
  const title = episode ? `${content?.title} · S${episode.seasonNumber} E${episode.episodeNumber}` : content?.title;
  const showSkip = intro.end > 0 && currentTime >= intro.start && currentTime < intro.end ? { label: "Пропустить заставку", time: intro.end } : credits.end > 0 && currentTime >= credits.start && currentTime < credits.end ? { label: "Пропустить концовку", time: credits.end } : null;

  useEffect(() => {
    if (sources[0] && !sources.some((source) => source._id === selectedSourceId)) setSelectedSourceId(sources[0]._id);
  }, [sources, selectedSourceId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!resumeTarget || !video || resumeTarget.episodeId !== (episode?._id || "") || video.readyState < 1) return;
    video.currentTime = Math.min(resumeTarget.position, Math.max(0, (video.duration || resumeTarget.position) - 1));
    setCurrentTime(video.currentTime);
    setResumeTarget(null);
  }, [episode, resumeTarget, selectedSource]);

  useEffect(() => () => window.clearTimeout(controlsTimerRef.current), []);

  useEffect(() => {
    const syncFullscreen = () => setIsFullscreen(document.fullscreenElement === stageRef.current);
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  useEffect(() => {
    window.clearTimeout(controlsTimerRef.current);
    if (playing) controlsTimerRef.current = window.setTimeout(() => setControlsVisible(false), 5000);
    else setControlsVisible(true);
    return () => window.clearTimeout(controlsTimerRef.current);
  }, [playing]);

  const revealControls = () => {
    setControlsVisible(true);
    window.clearTimeout(controlsTimerRef.current);
    if (playing) controlsTimerRef.current = window.setTimeout(() => setControlsVisible(false), 5000);
  };

  const togglePlay = () => videoRef.current?.paused ? videoRef.current.play() : videoRef.current?.pause();
  const seek = (value) => { if (videoRef.current) videoRef.current.currentTime = Number(value); };
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await stageRef.current?.requestFullscreen?.();
    } catch (_error) {}
  };
  const persistProgress = (video, force = false) => {
    if (!progressEnabledRef.current || !content?._id || !video?.duration || video.currentTime < 2) return;
    const now = Date.now();
    if (!force && now - lastProgressSaveRef.current < 10000) return;
    lastProgressSaveRef.current = now;
    authApi.saveWatchProgress(content._id, { episodeId: episode?._id || "", position: video.currentTime, duration: video.duration }).catch(() => {});
  };
  const changeQuality = (sourceId) => {
    resumeAtRef.current = videoRef.current?.currentTime || 0;
    setSelectedSourceId(sourceId);
  };
  const selectEpisode = (episodeId) => { persistProgress(videoRef.current, true); setSelectedEpisodeId(episodeId); setCurrentTime(0); setPlaying(false); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const onLoaded = () => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(video.duration || 0);
    video.playbackRate = playbackRate;
    video.volume = volume;
    if (resumeTarget && resumeTarget.episodeId === (episode?._id || "")) {
      video.currentTime = Math.min(resumeTarget.position, Math.max(0, video.duration - 1));
      setCurrentTime(video.currentTime);
      setResumeTarget(null);
    } else if (resumeAtRef.current) {
      video.currentTime = resumeAtRef.current;
      resumeAtRef.current = 0;
      video.play().catch(() => {});
    }
  };

  if (error) return <main className="player-shell player-state"><Link to="/" className="player-back"><ArrowLeft size={18}/> На главную</Link><div className="player-error"><h1>Не удалось открыть видео</h1><p>{error}</p></div></main>;
  if (!content) return <main className="player-shell player-state"><div className="player-loader"/><p>Готовим кинозал…</p></main>;

  return <main className="player-shell">
    <Header />
    <div className="player-detail-backdrop" style={{ backgroundImage: `url(${mediaUrl(content.backdropUrl || content.posterUrl)})` }}/>
    <div className="player-ambient" style={{ backgroundImage: `url(${mediaUrl(content.backdropUrl || content.posterUrl)})` }}/>
    <header className="player-header">
      <Link to={content.type === "series" ? "/series" : "/movies"} className="player-back"><ArrowLeft size={18}/> Назад в каталог</Link>
      <div className="player-now"><span>Сейчас смотрите</span><strong>{title}</strong></div>
    </header>

    <section className="player-details">{content.posterUrl ? <img src={mediaUrl(content.posterUrl)} alt={content.title}/> : <div className="player-poster-empty"><ListVideo size={34}/></div>}<div><span className="player-kicker">{content.type === "series" ? "Сериал" : "Фильм"}</span><h1>{content.title}</h1>{content.originalTitle && <h2>{content.originalTitle}</h2>}<div className="player-tags"><span>{content.releaseDate ? new Date(content.releaseDate).getFullYear() : "Новинка"}</span><span>{content.ageRating || "16+"}</span>{content.duration > 0 && <span>{Math.round(content.duration / 60)} мин</span>}{content.rating > 0 && <span className="rating">★ {Number(content.rating).toFixed(1)}</span>}{content.genres?.map((genre) => <span key={genre}>{genre}</span>)}</div><div className="player-description"><span>О фильме</span><p>{content.description || "Описание скоро появится."}</p></div>{content.countries?.length > 0 && <small className="player-countries">{content.countries.join(" · ")}</small>}</div></section>

    <div className={`player-layout ${content.type === "series" ? "with-episodes" : ""}`}>
      <section ref={stageRef} className={`player-stage ${controlsVisible ? "controls-visible" : "controls-hidden"}`} onMouseMove={revealControls} onMouseEnter={revealControls} onTouchStart={revealControls}>
        {selectedSource ? <video ref={videoRef} key={selectedSource._id} src={mediaUrl(selectedSource.url)} poster={mediaUrl(content.backdropUrl || content.posterUrl)} onClick={togglePlay} onLoadedMetadata={onLoaded} onTimeUpdate={(e) => { setCurrentTime(e.currentTarget.currentTime); persistProgress(e.currentTarget); }} onPlay={() => setPlaying(true)} onPause={(e) => { setPlaying(false); persistProgress(e.currentTarget, true); }} onWaiting={() => setWaiting(true)} onPlaying={() => setWaiting(false)} onEnded={(e) => { setPlaying(false); persistProgress(e.currentTarget, true); }}/> : <div className="player-empty"><ListVideo size={42}/><h2>Видео пока не загружено</h2><p>Выберите другой эпизод или вернитесь позже.</p></div>}
        {selectedSource && <>
          <div className="player-overlay-brand">{content.logoUrl ? <img src={mediaUrl(content.logoUrl)} alt={content.title}/> : <strong>{content.title}</strong>}{episode && <span>S{episode.seasonNumber} · E{episode.episodeNumber}</span>}</div>
          <button className={`player-center-play ${playing ? "is-playing" : ""}`} onClick={togglePlay} aria-label={playing ? "Пауза" : "Воспроизвести"}>{waiting ? <span className="player-loader"/> : playing ? <Pause size={32} fill="currentColor"/> : <Play size={36} fill="currentColor"/>}</button>
          {showSkip && <button className="player-skip" onClick={() => seek(showSkip.time)}><SkipForward size={18}/>{showSkip.label}</button>}
          <div className="player-video-controls">
            <input className="player-progress" type="range" min="0" max={duration || 0} step="0.1" value={currentTime} onChange={(e) => seek(e.target.value)} style={{ "--watched": `${duration ? currentTime / duration * 100 : 0}%` }}/>
            <div className="player-control-row"><button onClick={togglePlay}>{playing ? <Pause size={20} fill="currentColor"/> : <Play size={20} fill="currentColor"/>}</button><button onClick={() => seek(Math.max(0,currentTime-10))}><RotateCcw size={19}/><small>10</small></button><div className="player-volume"><button onClick={() => { const video=videoRef.current; if(video){video.muted=!video.muted;setMuted(video.muted);}}}>{muted || volume === 0 ? <VolumeX size={21}/> : <Volume2 size={21}/>}</button><input aria-label="Громкость" type="range" min="0" max="1" step="0.01" value={muted ? 0 : volume} onChange={(event) => { const next=Number(event.target.value);setVolume(next);setMuted(false);if(videoRef.current){videoRef.current.volume=next;videoRef.current.muted=false;}}}/></div><span>{formatTime(currentTime)} <i>/</i> {formatTime(duration)}</span><strong>{title}</strong><label className="player-control-select"><span>{selectedSource?.quality || "Auto"}</span><select aria-label="Качество" value={selectedSource?._id || ""} onChange={(event) => changeQuality(event.target.value)}>{sources.map((source) => <option key={source._id} value={source._id}>{source.quality || "Original"}</option>)}</select><ChevronDown size={14}/></label><label className="player-control-select"><span>{playbackRate}×</span><select aria-label="Скорость" value={playbackRate} onChange={(event) => {const rate=Number(event.target.value);setPlaybackRate(rate);if(videoRef.current)videoRef.current.playbackRate=rate;}}>{[0.5,0.75,1,1.25,1.5,2].map((rate)=><option key={rate} value={rate}>{rate}×</option>)}</select><ChevronDown size={14}/></label><button onClick={toggleFullscreen} aria-label={isFullscreen ? "Выйти из полноэкранного режима" : "На весь экран"}>{isFullscreen ? <Minimize size={21}/> : <Expand size={21}/>}</button></div>
          </div>
        </>}
      </section>

      {content.type === "series" && <aside className="player-episodes"><div><span>Эпизоды</span><strong>{episodes.length} серий</strong></div><div className="player-episode-list">{episodes.map((item) => <button key={item._id} className={item._id === episode?._id ? "active" : ""} onClick={() => selectEpisode(item._id)}><i>{item.episodeNumber}</i><span><strong>{item.title || `Серия ${item.episodeNumber}`}</strong><small>Сезон {item.seasonNumber}{item.duration ? ` · ${Math.round(item.duration/60)} мин` : ""}</small></span>{item._id === episode?._id && <Play size={15} fill="currentColor"/>}</button>)}</div></aside>}
    </div>

    <div className="player-footer"><Footer /></div>
  </main>;
}
